/**
 * Shared (app) Config Service
 * ============================
 *
 * Both libs have there own config service ; this one is for our Apps
 *
 * - import environment config the same way in any apps
 * - share some config between apps (i18n...)
 * - and already accept extra configs
 *
 * The idea is to use this one (generic) in any apps, by injecting our app config type.
 * Exept if bug app with lot of specific config management, you may use a specific config service
 *
 * - merge .env with default
 * - then merge config with default, env and platformConfig
 * - then compute extra config clean (i18n)
 *
 * > This is shared only in our connect platform monoorepo
 * >
 * > - it is not for exxternal applications.
 * > - and we assume it will be used for ~~legacy~~ connect mode.
 */

import { Inject, inject, Injectable, Optional } from '@angular/core';
import { EMPTY, Observable, of } from 'rxjs';

import {
  AuthInterceptorExclusionService,
  DefaultDomain,
  PlatformConfigService,
} from '@evc/platform';
import { ConfigService, deepMerge, ENV_VARIABLES } from '@evc/web-components';

import { defaultSharedConfigFactory } from './config.default';
import type { SharedConfig, SharedEnv } from './config.type';

@Injectable({
  providedIn: 'root',
})
export class SharedConfigService<
  T extends SharedEnv = SharedEnv,
  U extends SharedConfig = SharedConfig,
> extends ConfigService<U> {
  #authExclusionService = inject(AuthInterceptorExclusionService);

  constructor(
    // eslint-disable-next-line @angular-eslint/prefer-inject
    @Optional() @Inject(ENV_VARIABLES) readonly envVars: T,
    // eslint-disable-next-line @angular-eslint/prefer-inject
    private readonly platformConfig: PlatformConfigService,
  ) {
    const environment = envVars ?? { ENV: 'production' };
    const config = deepMerge<U>(platformConfig.config(), defaultSharedConfigFactory(environment));

    super(config, environment);
  }

  /** safe way to check an environment variable
   * use readonly configuration : not affected by this.set
   */
  getEnv<K extends keyof T>(key: K, autoFormat = true): T[K] {
    const value = this.envVars?.[key];

    if (!autoFormat) return value;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const autoValue = value as any;
    if ([true, 'true', '1', 1].includes(autoValue)) return true as T[K];
    if ([false, 'false', '0', 0].includes(autoValue)) return false as T[K];
    if (autoValue === '') return null as T[K];

    try {
      const tryNumber = parseInt(autoValue, 10);
      if (!isNaN(tryNumber)) return tryNumber as T[K];
    } catch {}

    return value;
  }

  /** return our uri prefixed with correct domain (per env)
   * safely because auto stop if no api config found (!useConnect)
   */
  safelyGetApiUrl$(
    domain: DefaultDomain,
    endpoint: string,
    options: GetApiUrlOptions = {},
  ): Observable<string> {
    const baseURI = this.get('api')?.uris[domain];

    if (!baseURI) return EMPTY;

    const fullUri = `${baseURI}${endpoint}`;

    if (options.skipAuth) {
      this.#authExclusionService.markAsUnprotected(fullUri);
    }

    return of(fullUri);
  }
}

export type GetApiUrlOptions = {
  skipAuth?: boolean;
};

export { ConfigService };
